import React, { useMemo, useState, useEffect } from 'react';
import { useDeals } from '../../context/DataContext';
import { fmtDate, fmtMoney, daysUntil } from '../../lib/format';
import { amountForView, dealTalentIds, isInvoicedDeal, emptyDeliverable, getContentStatus, getContentStatusMeta, toggleContentStatus, DELIVERABLE_PLATFORM_PRESETS } from '../../lib/deals';
import { useRevenueView } from '../../context/RevenueViewContext';
import { Trash2, Link as LinkIcon, FileText, PoundSterling, Send, Plus, ChevronDown, Repeat } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import DeliverablesCell from '../DeliverablesCell';
import InitialsAvatar from '../InitialsAvatar';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import ContentStatusChecklist from '../ContentStatusChecklist';
import { toast } from 'sonner';

const StagePill = ({ stage }) => {
  const color = stage?.color || '#94a3b8';
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: `${color}22`, color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {stage?.label}
    </span>
  );
};

const InlineValueCell = ({ deal, canEdit }) => {
  const { updateDeal } = useDeals();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const displayed = Number(deal.value) || 0;

  const startEdit = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    setDraft(String(deal.value ?? ''));
    setEditing(true);
  };
  const commit = async () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n === deal.value) { setEditing(false); return; }
    await updateDeal(deal.id, { value: n });
    setEditing(false);
  };
  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <input
        type="number"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
        onClick={(e) => e.stopPropagation()}
        className="w-24 h-7 text-right text-sm font-semibold border border-pink-300 rounded px-1.5 outline-none focus:ring-2 focus:ring-pink-200"
        data-testid={`inline-value-input-${deal.id}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={startEdit}
      title={canEdit ? 'click to edit' : ''}
      className={`font-semibold text-slate-900 ${canEdit ? 'hover:bg-slate-100 rounded px-1.5 py-0.5 -mx-1.5 cursor-pointer' : ''}`}
      data-testid={`inline-value-${deal.id}`}
    >
      {fmtMoney(displayed, deal.currency)}
    </button>
  );
};

const InlineStageSelect = ({ deal, stages, stageMap, canEdit }) => {
  const { updateDeal } = useDeals();
  const stage = stageMap[deal.stage];
  const color = stage?.color || '#94a3b8';
  if (!canEdit) return <StagePill stage={stage} />;
  return (
    <Select value={deal.stage} onValueChange={(v) => updateDeal(deal.id, { stage: v })}>
      <SelectTrigger
        className="h-7 px-2 py-0 w-auto min-w-[120px] text-[11px] font-medium border-0 rounded-full [&>svg]:opacity-60"
        style={{ background: `${color}22`, color }}
        onClick={(e) => e.stopPropagation()}
        data-testid={`inline-stage-${deal.id}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent onClick={(e) => e.stopPropagation()}>
        {stages.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
              <span style={{ color: s.color }}>{s.label}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const PaymentPills = ({ deal, canEdit }) => {
  const { updateDeal } = useDeals();
  const items = [
    { key: 'invoiced_at', label: 'invoiced', Icon: FileText, on: '#38bdf8' },
    { key: 'paid_in_at', label: 'paid in', Icon: PoundSterling, on: '#a78bfa' },
    { key: 'paid_out_at', label: 'paid out', Icon: Send, on: '#059669' },
  ];
  const toggle = (key) => {
    if (!canEdit) return;
    const val = deal[key] ? '' : todayISO();
    updateDeal(deal.id, { [key]: val });
  };
  return (
    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {items.map(({ key, label, Icon, on }) => {
        const active = Boolean(deal[key]);
        return (
          <button
            key={key}
            type="button"
            disabled={!canEdit}
            onClick={() => toggle(key)}
            title={active ? `${label} · ${deal[key]}` : `mark ${label}`}
            style={active ? { background: on + '22', color: on, borderColor: on + '55' } : {}}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${active ? '' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'} ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
            data-testid={`pay-${key}-${deal.id}`}
          >
            <Icon className="w-3 h-3" />
            {label}
          </button>
        );
      })}
    </div>
  );
};

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// Colour-coded per calendar month (soft, on-brand palette; light bg + darker ink).
const MONTH_STYLES = [
  { bg: '#fee2e2', ink: '#991b1b' }, // Jan — red
  { bg: '#ffe4d1', ink: '#7c2d12' }, // Feb — peach
  { bg: '#fdefc7', ink: '#78350f' }, // Mar — amber
  { bg: '#fef9c3', ink: '#713f12' }, // Apr — yellow
  { bg: '#dcfce7', ink: '#166534' }, // May — mint
  { bg: '#d1fae5', ink: '#065f46' }, // Jun — emerald
  { bg: '#cffafe', ink: '#155e75' }, // Jul — cyan
  { bg: '#dbeafe', ink: '#1e40af' }, // Aug — blue
  { bg: '#ede9fe', ink: '#5b21b6' }, // Sep — violet
  { bg: '#f3e8ff', ink: '#6b21a8' }, // Oct — purple
  { bg: '#fce7f3', ink: '#9d174d' }, // Nov — pink
  { bg: '#e0e7ff', ink: '#3730a3' }, // Dec — indigo
];
const monthStyleFor = (deal) => {
  if (!deal?.created_at) return { bg: '#f1f5f9', ink: '#475569' };
  const d = new Date(deal.created_at);
  return MONTH_STYLES[d.getMonth()] || { bg: '#f1f5f9', ink: '#475569' };
};
const dealMonthCell = (deal) => {
  if (!deal?.created_at) return '—';
  const d = new Date(deal.created_at);
  return MONTHS_SHORT[d.getMonth()];
};
const dealMonthTooltip = (deal) => {
  if (!deal?.created_at) return '';
  const d = new Date(deal.created_at);
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

const InlineMonthCell = ({ deal, canEdit }) => {
  const { updateDeal } = useDeals();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const currentYm = () => {
    if (!deal.created_at) return '';
    const d = new Date(deal.created_at);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const start = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    setDraft(currentYm());
    setEditing(true);
  };
  const commit = async (value) => {
    setEditing(false);
    if (!value) return;
    // Preserve the day of month if possible, else use the 1st
    const existing = deal.created_at ? new Date(deal.created_at) : new Date();
    const [y, m] = value.split('-').map(Number);
    const day = existing.getDate();
    const daysInNew = new Date(y, m, 0).getDate();
    const safeDay = Math.min(day, daysInNew);
    const iso = new Date(Date.UTC(y, m - 1, safeDay, 12)).toISOString();
    if (iso !== deal.created_at) await updateDeal(deal.id, { created_at: iso });
  };

  if (editing) {
    return (
      <input
        type="month"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(e.target.value); if (e.key === 'Escape') setEditing(false); }}
        onClick={(e) => e.stopPropagation()}
        className="h-6 text-xs border border-pink-300 rounded px-1 outline-none focus:ring-2 focus:ring-pink-200"
        data-testid={`inline-month-input-${deal.id}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      title={canEdit ? `${dealMonthTooltip(deal)} · click to change month` : dealMonthTooltip(deal)}
      className={`inline-flex items-center rounded-full text-[11px] font-semibold px-2.5 py-0.5 border tracking-wide ${canEdit ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}`}
      style={{ background: monthStyleFor(deal).bg, color: monthStyleFor(deal).ink, borderColor: monthStyleFor(deal).ink + '20' }}
      data-testid={`inline-month-${deal.id}`}
    >
      {dealMonthCell(deal)}
    </button>
  );
};

const normalizeUrl = (u) => {
  if (!u) return '';
  const s = String(u).trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

const TalentInline = ({ talents }) => {
  if (!talents.length) return <span className="text-slate-300">—</span>;
  const visible = talents.slice(0, 3);
  const overflow = talents.length - visible.length;
  const label = talents.length === 1
    ? talents[0].name
    : `${talents[0].name} + ${talents.length - 1}`;
  return (
    <div className="flex items-center gap-2" title={talents.map((t) => t.name).join(', ')}>
      <div className="flex items-center -space-x-1.5">
        {visible.map((t) => (
          <InitialsAvatar key={t?.user_id || Math.random()} name={t?.name} size={24} className="ring-2 ring-white text-[9px]" />
        ))}
        {overflow > 0 && (
          <div className="w-6 h-6 rounded-full bg-slate-100 ring-2 ring-white flex items-center justify-center text-[9px] text-slate-600 font-medium">+{overflow}</div>
        )}
      </div>
      <span className="text-xs text-slate-600 truncate max-w-[140px]">{label}</span>
    </div>
  );
};

const InlineTextCell = ({ deal, field, placeholder, canEdit, renderDisplay, className = '', multiline = false }) => {
  const { updateDeal } = useDeals();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const start = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    setDraft(String(deal[field] ?? ''));
    setEditing(true);
  };
  const commit = async () => {
    setEditing(false);
    const next = multiline ? draft : draft.trim();
    if (next === (deal[field] || '')) return;
    await updateDeal(deal.id, { [field]: next });
  };
  if (editing) {
    if (multiline) {
      return (
        <textarea
          autoFocus
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
          onClick={(e) => e.stopPropagation()}
          rows={3}
          className="text-xs border border-pink-300 rounded p-1.5 outline-none focus:ring-2 focus:ring-pink-200 w-full max-w-[200px] resize-none"
          data-testid={`inline-${field}-input-${deal.id}`}
        />
      );
    }
    return (
      <input
        type="text"
        autoFocus
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        onClick={(e) => e.stopPropagation()}
        className="h-7 text-xs border border-pink-300 rounded px-1.5 outline-none focus:ring-2 focus:ring-pink-200 w-full max-w-[180px]"
        data-testid={`inline-${field}-input-${deal.id}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      title={canEdit ? 'click to edit' : ''}
      className={`${canEdit ? 'hover:bg-slate-100 rounded cursor-pointer' : ''} ${className}`}
      data-testid={`inline-${field}-${deal.id}`}
    >
      {renderDisplay(deal[field])}
    </button>
  );
};

const InlineDateCell = ({ deal, field, canEdit, overdue }) => {
  const { updateDeal } = useDeals();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const toYmd = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };
  const start = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    setDraft(toYmd(deal[field]));
    setEditing(true);
  };
  const commit = async (value) => {
    setEditing(false);
    const iso = value ? new Date(value + 'T12:00:00Z').toISOString() : null;
    if ((iso || null) === (deal[field] || null)) return;
    await updateDeal(deal.id, { [field]: iso });
  };
  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(e.target.value); if (e.key === 'Escape') setEditing(false); }}
        onClick={(e) => e.stopPropagation()}
        className="h-7 text-xs border border-pink-300 rounded px-1.5 outline-none focus:ring-2 focus:ring-pink-200"
        data-testid={`inline-${field}-input-${deal.id}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      title={canEdit ? 'click to change date' : ''}
      className={`${canEdit ? 'hover:bg-slate-100 rounded px-1.5 py-0.5 -mx-1.5 cursor-pointer' : ''} ${overdue ? 'text-red-600 font-medium' : 'text-slate-600'} text-xs`}
      data-testid={`inline-${field}-${deal.id}`}
    >
      {deal[field] ? fmtDate(deal[field]) : <span className="text-slate-300">—</span>}
    </button>
  );
};

const InlineContactSelect = ({ deal, brand, canEdit }) => {
  const { updateDeal } = useDeals();
  const contacts = brand?.contacts || [];
  const c = contacts.find((x) => x.id === deal.contact_id);
  if (!canEdit || contacts.length === 0) {
    if (!c) return <span className="text-slate-300 text-xs">—</span>;
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full border border-slate-200 text-xs text-slate-700 bg-white whitespace-nowrap" title={`${c.name}${c.email ? ' · ' + c.email : ''}`}>
        {c.name}
      </span>
    );
  }
  return (
    <Select
      value={deal.contact_id || '__none__'}
      onValueChange={(v) => updateDeal(deal.id, { contact_id: v === '__none__' ? null : v })}
    >
      <SelectTrigger
        className="h-7 px-2 py-0 w-auto min-w-[130px] text-xs bg-white border border-slate-200 rounded-full"
        onClick={(e) => e.stopPropagation()}
        data-testid={`inline-contact-${deal.id}`}
      >
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent onClick={(e) => e.stopPropagation()}>
        <SelectItem value="__none__"><span className="text-slate-400">no contact</span></SelectItem>
        {contacts.map((k) => (
          <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const ContactCell = ({ deal, brand }) => {
  const c = (brand?.contacts || []).find((x) => x.id === deal.contact_id);
  if (!c) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full border border-slate-200 text-xs text-slate-700 bg-white whitespace-nowrap"
      title={`${c.name}${c.email ? ' · ' + c.email : ''}`}
    >
      {c.name}
    </span>
  );
};

const ReadyToInvoiceToggle = ({ deal, canEdit }) => {
  const { updateDeal } = useDeals();
  const on = Boolean(deal.ready_to_invoice);
  return (
    <input
      type="checkbox"
      checked={on}
      disabled={!canEdit}
      onChange={async (e) => { await updateDeal(deal.id, { ready_to_invoice: e.target.checked }); }}
      onClick={(e) => e.stopPropagation()}
      className="w-4 h-4 accent-pink-500 cursor-pointer"
      data-testid={`ready-to-invoice-${deal.id}`}
      title={on ? 'ready — appears in invoicing tab' : 'mark ready to invoice'}
    />
  );
};

const InlineDeliverablesText = ({ deal, onSave, isManager }) => {
  const [draft, setDraft] = useState(deal.type || '');
  useEffect(() => { setDraft(deal.type || ''); }, [deal.type]);
  return (
    <input
      value={draft}
      disabled={!isManager}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if ((draft || '') !== (deal.type || '')) onSave(draft); }}
      placeholder="deliverables…"
      className="h-6 text-[11px] px-2 rounded-full bg-pink-50 text-pink-700 placeholder:text-pink-300 border border-pink-100 w-[150px] focus:outline-none focus:border-pink-300 disabled:opacity-60"
      data-testid={`deal-deliverables-text-${deal.id}`}
    />
  );
};

const DeliverablePlatformsPicker = ({ deliverable, onPatch, isManager, variant = 'lilac' }) => {
  const [customDraft, setCustomDraft] = useState('');
  const platforms = deliverable.platforms || [];
  const options = Array.from(new Set([...DELIVERABLE_PLATFORM_PRESETS, ...platforms]));
  const toggle = (label) => {
    onPatch({ platforms: platforms.includes(label) ? platforms.filter((p) => p !== label) : [...platforms, label] });
  };
  const addCustom = () => {
    const val = customDraft.trim();
    if (!val) return;
    if (!platforms.includes(val)) onPatch({ platforms: [...platforms, val] });
    setCustomDraft('');
  };
  const triggerCls = variant === 'pink'
    ? "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium max-w-[160px] disabled:opacity-60"
    : "inline-flex items-center h-6 px-2 rounded-full text-[10px] font-medium bg-white border border-lilac-200 text-lilac-700 max-w-[110px] disabled:opacity-60";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!isManager}
          className={triggerCls}
          style={variant === 'pink' ? { background: '#fce7f3', color: '#be185d' } : undefined}
          data-testid={`deliverable-platforms-${deliverable.id}`}
        >
          <span className="truncate">{platforms.length ? platforms.join(', ') : '+ platform'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-2">
        <div className="flex flex-col gap-0.5">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={platforms.includes(opt)} onChange={() => toggle(opt)} className="accent-pink-500" data-testid={`deliverable-platform-opt-${deliverable.id}-${opt}`} />
              {opt}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
          <input
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustom(); }}
            placeholder="add custom…"
            className="h-7 text-xs border border-slate-200 rounded-md px-2 flex-1 outline-none focus:border-pink-300"
            data-testid={`deliverable-platform-custom-input-${deliverable.id}`}
          />
          <button type="button" onClick={addCustom} className="p-1 rounded-md text-pink-600 hover:bg-pink-50" data-testid={`deliverable-platform-custom-add-${deliverable.id}`}>
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const DealDeliverableRow = ({ deliverable, index, onPatch, onRemove, isManager }) => {
  const meta = getContentStatusMeta(getContentStatus(deliverable));
  const toggle = (id) => onPatch(toggleContentStatus(deliverable, id));
  const inputCls = "h-7 text-xs border border-slate-200 rounded-md px-2 outline-none focus:border-pink-300 disabled:bg-slate-50 disabled:text-slate-400 w-full";
  return (
    <tr className="bg-lilac-50/40 border-b border-lilac-100" data-testid={`deliverable-row-${deliverable.id}`}>
      {/* month */}
      <td className="px-3 py-1.5"></td>
      {/* deal — deliverable name */}
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-300 text-xs shrink-0">↳</span>
          <input
            value={deliverable.title || ''}
            onChange={(e) => onPatch({ title: e.target.value })}
            placeholder={`deliverable ${index + 1} — e.g. reel 1`}
            disabled={!isManager}
            className={inputCls}
            data-testid={`deliverable-title-${deliverable.id}`}
          />
        </div>
      </td>
      {/* brand */}
      <td className="px-3 py-1.5 text-center text-slate-300 text-xs">—</td>
      {/* contact */}
      <td className="px-3 py-1.5 text-center text-slate-300 text-xs">—</td>
      {/* status */}
      <td className="px-3 py-1.5 text-center text-slate-300 text-xs">—</td>
      {/* drive */}
      <td className="px-3 py-1.5 text-center text-slate-300 text-xs">—</td>
      {/* content status */}
      <td className="px-3 py-1.5">
        <div className="flex justify-center">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-7 px-2 rounded-full text-[11px] font-medium min-w-[130px]"
                style={{ background: `${meta.color}22`, color: meta.color }}
                data-testid={`deliverable-status-${deliverable.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.color }} />
                <span className="truncate">{meta.label}</span>
                <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 p-1">
              <ContentStatusChecklist deal={deliverable} isManager={isManager} onToggle={toggle} />
            </PopoverContent>
          </Popover>
        </div>
      </td>
      {/* deliverables — index label + platforms picker */}
      <td className="px-3 py-1.5 text-center">
        <div className="flex flex-col items-center gap-1">
          <span className="text-[9px] text-lilac-500 uppercase tracking-wide">deliverable {index + 1}</span>
          <DeliverablePlatformsPicker deliverable={deliverable} onPatch={onPatch} isManager={isManager} />
        </div>
      </td>
      {/* full rate */}
      <td className="px-3 py-1.5">
        <input
          type="number"
          value={deliverable.value || ''}
          onChange={(e) => onPatch({ value: Number(e.target.value) || 0 })}
          placeholder="£0"
          disabled={!isManager}
          className={inputCls}
          title="deliverable rate"
          data-testid={`deliverable-value-${deliverable.id}`}
        />
      </td>
      {/* talent rate */}
      <td className="px-3 py-1.5 text-center text-slate-300 text-xs">—</td>
      {/* agency fee */}
      <td className="px-3 py-1.5 text-center text-slate-300 text-xs">—</td>
      {/* content due */}
      <td className="px-3 py-1.5">
        <input
          type="date"
          value={deliverable.due_date || ''}
          onChange={(e) => onPatch({ due_date: e.target.value })}
          disabled={!isManager}
          className={inputCls}
          title="content due"
          data-testid={`deliverable-due-date-${deliverable.id}`}
        />
      </td>
      {/* content live */}
      <td className="px-3 py-1.5">
        <input
          type="date"
          value={deliverable.go_live_date || ''}
          onChange={(e) => onPatch({ go_live_date: e.target.value })}
          disabled={!isManager}
          className={inputCls}
          title="content live"
          data-testid={`deliverable-live-date-${deliverable.id}`}
        />
      </td>
      {/* caption */}
      <td className="px-3 py-1.5">
        <input
          value={deliverable.caption || ''}
          onChange={(e) => onPatch({ caption: e.target.value })}
          placeholder="caption…"
          disabled={!isManager}
          className={inputCls}
          data-testid={`deliverable-caption-${deliverable.id}`}
        />
      </td>
      {/* notes */}
      <td className="px-3 py-1.5">
        <input
          value={deliverable.notes || ''}
          onChange={(e) => onPatch({ notes: e.target.value })}
          placeholder="notes…"
          disabled={!isManager}
          className={inputCls}
          data-testid={`deliverable-notes-${deliverable.id}`}
        />
      </td>
      {/* PO number */}
      <td className="px-3 py-1.5 text-center text-slate-300 text-xs">—</td>
      {/* ready to invoice */}
      <td className="px-3 py-1.5 text-center">
        <input
          type="checkbox"
          checked={Boolean(deliverable.ready_to_invoice)}
          disabled={!isManager}
          onChange={(e) => onPatch({ ready_to_invoice: e.target.checked })}
          className="w-4 h-4 accent-pink-500 cursor-pointer"
          data-testid={`deliverable-ready-to-invoice-${deliverable.id}`}
          title={deliverable.ready_to_invoice ? 'ready — appears in invoicing tab' : 'mark ready to invoice'}
        />
      </td>
      {/* delete */}
      <td className="px-2 py-1.5 text-center">
        {isManager && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
            data-testid={`deliverable-remove-${deliverable.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
};

const DealRow = ({ deal, view, stageMap, stages, talentMap, brandMap, isManager, removeDeal, onCardClick, hideTalent = false }) => {
  const { updateDeal } = useDeals();
  const clearReview = (e, field) => { e.stopPropagation(); updateDeal(deal.id, { [field]: false }); };
  const [deliverablesOpen, setDeliverablesOpen] = useState(false);
  const talents = dealTalentIds(deal).map((id) => talentMap[id]).filter(Boolean);
  const primaryTalent = talents[0] || null;
  const b = deal.brand_id ? brandMap[deal.brand_id] : null;
  const dueIn = daysUntil(deal.due_date);
  const overdue = dueIn !== null && dueIn < 0 && deal.stage !== 'complete';
  const href = normalizeUrl(deal.link);
  const fullRate = Number(deal.value) || 0;
  const commissionPct = Number(deal.commission_percent ?? primaryTalent?.commission_percent) || 0;
  const agencyFee = fullRate * (commissionPct / 100);
  const talentRate = fullRate - agencyFee;
  const invoiced = isInvoicedDeal(deal);
  const deliverables = deal.deliverables || [];
  const patchDeliverable = async (idx, patch) => {
    const result = await updateDeal(deal.id, { deliverables: deliverables.map((d, i) => (i === idx ? { ...d, ...patch } : d)) });
    if (result?._marked_ready_to_invoice) {
      toast.success('marked ready to invoice', { description: 'now suggested on the talent invoicing tab' });
    }
  };
  const addDeliverable = () => {
    updateDeal(deal.id, { deliverables: [...deliverables, emptyDeliverable()], retainer: true });
  };
  const removeDeliverable = (idx) => {
    const next = deliverables.filter((_, i) => i !== idx);
    updateDeal(deal.id, { deliverables: next, retainer: next.length > 0 });
  };
  return (
    <>
    <tr
      className={`cursor-pointer whitespace-nowrap text-center ${invoiced ? 'hover:brightness-95' : 'hover:bg-pink-50/30'}`}
      style={{ borderTop: '1px solid #f1f5f9', background: invoiced ? '#d9f5e6' : undefined }}
      onClick={() => onCardClick(deal)}
      title={invoiced ? 'invoiced' : undefined}
      data-testid={invoiced ? `deal-row-invoiced-${deal.id}` : `deal-row-${deal.id}`}
    >
      {/* month */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center"><InlineMonthCell deal={deal} canEdit={isManager} /></div>
      </td>
      {/* deal */}
      <td className="px-3 py-2">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200 hover:bg-slate-200 transition-colors max-w-[220px] truncate" title={deal.title}>
          {deal.title}
        </span>
      </td>
      {/* brand */}
      <td className="px-3 py-2 text-slate-700">
        {b ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-slate-200 text-xs text-slate-700 bg-white">
            {b.logo && <span>{b.logo}</span>}
            {b.name}
          </span>
        ) : <span className="italic text-slate-400 text-xs">no brand</span>}
      </td>
      {/* contact */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center"><InlineContactSelect deal={deal} brand={b} canEdit={isManager} /></div>
      </td>
      {/* status (stage) */}
      <td className={`px-3 py-2 ${isManager && deal.status_review ? 'bg-red-50' : ''}`}>
        <div className="flex flex-col items-center gap-0.5">
          <InlineStageSelect deal={deal} stages={stages} stageMap={stageMap} canEdit={isManager} />
          {isManager && deal.status_review && (
            <button onClick={(e) => clearReview(e, 'status_review')} className="text-[9px] font-bold text-red-600 hover:text-red-700 underline" data-testid={`row-confirm-status-${deal.id}`}>talent changed — confirm</button>
          )}
        </div>
      </td>
      {/* drive */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center items-center gap-1">
          {href && (
            <a href={href} target="_blank" rel="noopener noreferrer" title={href} className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-200 bg-white hover:border-pink-300 hover:text-pink-600 text-slate-500" data-testid={`deal-link-${deal.id}`}>
              <LinkIcon className="w-3.5 h-3.5" />
            </a>
          )}
          {isManager && !href && (
            <InlineTextCell
              deal={deal}
              field="link"
              canEdit={isManager}
              placeholder="paste url"
              className="text-[11px] text-slate-400 hover:text-slate-600 px-1.5 py-0.5"
              renderDisplay={() => <span className="text-slate-400 text-xs">+ add</span>}
            />
          )}
          {!isManager && !href && <span className="text-slate-300 text-xs">—</span>}
        </div>
      </td>
      {/* content status */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center"><DeliverablesCell deal={deal} /></div>
      </td>
      {/* deliverables (deal.type) + retainer multi-deliverable toggle */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center gap-1">
          {isManager && !(deal.retainer && deliverables.length > 0) && (
            <button
              type="button"
              onClick={addDeliverable}
              title="retainer — add a deliverable"
              className="p-1 rounded-md text-slate-400 hover:text-pink-600 hover:bg-pink-50 shrink-0"
              data-testid={`add-deliverable-${deal.id}`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          {deal.retainer && deliverables.length > 0 && (
            <button
              type="button"
              onClick={() => setDeliverablesOpen((v) => !v)}
              title={deliverablesOpen ? 'condense deliverables' : `reopen ${deliverables.length} deliverables`}
              className="p-1 rounded-md text-lilac-500 hover:text-lilac-700 hover:bg-lilac-50 shrink-0"
              data-testid={`toggle-deliverables-${deal.id}`}
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${deliverablesOpen ? '' : '-rotate-90'}`} />
            </button>
          )}
          <InlineDeliverablesText deal={deal} onSave={(v) => updateDeal(deal.id, { type: v })} isManager={isManager} />
        </div>
      </td>
      {/* full rate */}
      <td className={`px-3 py-2 ${isManager && deal.rate_review ? 'bg-red-50' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col items-center gap-0.5">
          <InlineValueCell deal={deal} canEdit={isManager} />
          {isManager && deal.rate_review && (
            <button onClick={(e) => clearReview(e, 'rate_review')} className="text-[9px] font-bold text-red-600 hover:text-red-700 underline" data-testid={`row-confirm-rate-${deal.id}`}>talent changed — confirm</button>
          )}
        </div>
      </td>
      {/* talent rate */}
      <td className="px-3 py-2 text-slate-700" title="full rate minus commission">{fmtMoney(talentRate, deal.currency)}</td>
      {/* agency fee */}
      <td className="px-3 py-2 text-slate-700" title={`${commissionPct}% commission`}>{fmtMoney(agencyFee, deal.currency)}</td>
      {/* content due */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center"><InlineDateCell deal={deal} field="due_date" canEdit={isManager} overdue={overdue} /></div>
      </td>
      {/* content live */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center"><InlineDateCell deal={deal} field="go_live_date" canEdit={isManager} /></div>
      </td>
      {/* caption — first line only, click row to see/edit full caption in the deal dialog */}
      <td className="px-3 py-2 max-w-[220px]">
        {deal.caption
          ? <span className="text-[11px] text-slate-600 truncate block text-left" title={deal.caption}>{deal.caption.split('\n')[0]}</span>
          : <span className="text-slate-300 text-[11px]">{isManager ? '+ caption' : '—'}</span>}
      </td>
      {/* notes */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <InlineTextCell
          deal={deal}
          field="notes"
          canEdit={isManager}
          placeholder="add a note…"
          className="text-xs text-slate-600 hover:text-slate-800 max-w-[220px] truncate inline-block align-middle"
          renderDisplay={(val) => (
            val ? (
              <span className="text-xs text-slate-700 truncate max-w-[200px] inline-block align-middle" title={val}>{val}</span>
            ) : (<span className="text-slate-300 text-xs">+ add</span>)
          )}
        />
      </td>
      {/* PO number */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center">
          <InlineTextCell
            deal={deal}
            field="po_number"
            canEdit={isManager}
            placeholder="PO number"
            className="text-xs text-slate-700 hover:text-slate-900 px-1.5 py-0.5"
            renderDisplay={(val) => (
              val ? <span className="text-xs text-slate-700" title={val}>{val}</span> : <span className="text-slate-300 text-xs">+ add</span>
            )}
          />
        </div>
      </td>
      {/* ready to invoice */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <ReadyToInvoiceToggle deal={deal} canEdit={isManager} />
      </td>
      {/* delete */}
      <td className="px-2 py-2">
        {isManager && (
          <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this deal?')) removeDeal(deal.id); }} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
    {deal.retainer && deliverables.length > 0 && (
      <>
        {deliverablesOpen && deliverables.map((dlv, idx) => (
          <DealDeliverableRow
            key={dlv.id}
            deliverable={dlv}
            index={idx}
            isManager={isManager}
            onPatch={(p) => patchDeliverable(idx, p)}
            onRemove={() => removeDeliverable(idx)}
          />
        ))}
        {deliverablesOpen && isManager && (
          <tr className="bg-lilac-50/40" onClick={(e) => e.stopPropagation()}>
            <td colSpan={18} className="px-6 py-1.5">
              <button
                type="button"
                onClick={addDeliverable}
                className="text-xs text-pink-600 hover:text-pink-700 inline-flex items-center gap-1"
                data-testid={`add-deliverable-inline-${deal.id}`}
              >
                <Plus className="w-3.5 h-3.5" /> add deliverable
              </button>
            </td>
          </tr>
        )}
      </>
    )}
    </>
  );
};

const HeaderRow = ({ hideTalent = false }) => (
  <thead className="bg-slate-50 sticky top-0 z-10" style={{ borderBottom: '1px solid #e2e8f0' }}>
    <tr className="text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '90px' }}>month</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '220px' }}>deal</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '140px' }}>brand</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '150px' }}>contact</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '140px' }}>status</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '70px' }}>drive</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '150px' }}>content status</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '140px' }}>deliverables</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '110px' }}>full rate</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '110px' }}>talent rate</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '110px' }}>agency fee</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '130px' }}>content due</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '130px' }}>content live</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '220px' }}>caption</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '180px' }}>notes</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '110px' }}>PO number</th>
      <th className="text-center font-semibold px-3 py-3" style={{ minWidth: '90px' }}>ready to invoice</th>
      <th className="w-10"></th>
    </tr>
  </thead>
);

const DealsTable = ({ filteredDeals, onCardClick, groupByTalent = false }) => {
  const { stages, talentMap, brandMap, removeDeal, talent } = useDeals();
  const { view } = useRevenueView();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const stageMap = Object.fromEntries(stages.map((s) => [s.id, s]));

  // Compute totals across the filtered deals (hook must run before any early return)
  const totals = useMemo(() => {
    let full = 0, tRate = 0, agency = 0;
    filteredDeals.forEach((d) => {
      const f = Number(d.value) || 0;
      const primaryTalent = talentMap[dealTalentIds(d)[0]] || null;
      const pct = Number(d.commission_percent ?? primaryTalent?.commission_percent) || 0;
      const af = f * (pct / 100);
      full += f; agency += af; tRate += (f - af);
    });
    return { full, talent: tRate, agency, count: filteredDeals.length };
  }, [filteredDeals, talentMap]);

  const grouped = useMemo(() => {
    if (!groupByTalent) return null;
    // Preserve talent order; unassigned last
    const buckets = new Map();
    talent.forEach((t) => buckets.set(t.user_id, { talent: t, deals: [] }));
    buckets.set('__none__', { talent: null, deals: [] });
    filteredDeals.forEach((d) => {
      const ids = dealTalentIds(d).filter((id) => buckets.has(id));
      if (ids.length === 0) {
        buckets.get('__none__').deals.push(d);
      } else {
        // Multi-talent deals appear in every relevant talent bucket (marked as shared)
        ids.forEach((id) => buckets.get(id).deals.push(d));
      }
    });
    return Array.from(buckets.values()).filter((g) => g.deals.length > 0);
  }, [groupByTalent, filteredDeals, talent]);

  if (grouped) {
    if (grouped.length === 0) {
      return (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400">no deals match your filters</div>
      );
    }
    return (
      <div className="space-y-4">
        {grouped.map(({ talent: t, deals }) => {
          // For group total, use amountForTalent so multi-talent deals attribute only their slice
          const total = deals.reduce((s, d) => {
            const ids = dealTalentIds(d);
            const base = amountForView(d, view, t);
            if (ids.length <= 1) return s + base;
            // multi-talent → attribute this talent's slice only
            const splits = d.talent_splits || {};
            const sum = ids.reduce((a, id) => a + (Number(splits[id]) || 0), 0);
            const share = sum > 0 ? ((Number(splits[t?.user_id]) || 0) / sum) : (1 / ids.length);
            return s + base * share;
          }, 0);
          // Row-sum totals (match the values shown in each row)
          const gTotals = deals.reduce((acc, d) => {
            const f = Number(d.value) || 0;
            const primary = talentMap[dealTalentIds(d)[0]] || null;
            const pct = Number(d.commission_percent ?? primary?.commission_percent) || 0;
            const af = f * (pct / 100);
            acc.full += f; acc.agency += af; acc.talent += (f - af);
            return acc;
          }, { full: 0, talent: 0, agency: 0 });
          return (
            <div key={t?.user_id || '__none__'} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-pink-200" style={{ background: '#d394ab' }}>
                {t ? (
                  <InitialsAvatar name={t.name} size={32} />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-white/30 border border-dashed border-white/50" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white truncate">{t?.name || 'unassigned'}</div>
                  <div className="text-xs text-white/80">{deals.length} deal{deals.length === 1 ? '' : 's'}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-white/80">{view === 'agency' ? 'agency' : view === 'talent' ? 'talent' : 'total'}</div>
                  <div className="text-sm font-semibold text-white">{fmtMoney(total)}</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="text-sm" style={{ minWidth: '1500px', width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                  <HeaderRow hideTalent />
                  <tbody>
                    {deals.map((deal) => (
                      <DealRow key={deal.id} deal={deal} view={view} stageMap={stageMap} stages={stages} talentMap={talentMap} brandMap={brandMap} isManager={isManager} removeDeal={removeDeal} onCardClick={onCardClick} hideTalent />
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-200 text-slate-800 font-semibold text-center whitespace-nowrap" data-testid={`deals-totals-row-${t?.user_id || 'none'}`}>
                    <tr>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3 text-right text-[11px] uppercase tracking-widest text-slate-500">totals ({deals.length})</td>
                      <td className="px-3 py-3">{fmtMoney(gTotals.full)}</td>
                      <td className="px-3 py-3">{fmtMoney(gTotals.talent)}</td>
                      <td className="px-3 py-3">{fmtMoney(gTotals.agency)}</td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Flat table fallback
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="text-sm" style={{ minWidth: '1500px', width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <HeaderRow />
          <tbody>
            {filteredDeals.map((deal) => (
              <DealRow key={deal.id} deal={deal} view={view} stageMap={stageMap} stages={stages} talentMap={talentMap} brandMap={brandMap} isManager={isManager} removeDeal={removeDeal} onCardClick={onCardClick} />
            ))}
            {filteredDeals.length === 0 && (
              <tr><td colSpan={17} className="text-center text-slate-400 py-10 text-sm">no deals match your filters</td></tr>
            )}
          </tbody>
          {filteredDeals.length > 0 && (
            <tfoot className="bg-slate-50 border-t-2 border-slate-200 text-slate-800 font-semibold text-center whitespace-nowrap" data-testid="deals-totals-row">
              <tr>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3 text-right text-[11px] uppercase tracking-widest text-slate-500">totals ({totals.count})</td>
                <td className="px-3 py-3">{fmtMoney(totals.full)}</td>
                <td className="px-3 py-3">{fmtMoney(totals.talent)}</td>
                <td className="px-3 py-3">{fmtMoney(totals.agency)}</td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default DealsTable;
